import { generatePath, useNavigate } from "react-router";
import { CloseIcon, MailIcon } from "vega-icons";
import { ListIllustration } from "vega-illustrations";

import { useGetDealTeaserEmailsSummaryV2 } from "@vega-api/generated/coinvest-deal-teaser-email-v2/coinvest-deal-teaser-email-v2";
import { type DealShortlistItemResponse } from "@vega-api/generated/types/dealShortlistItemResponse";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { DataGrid } from "@vega-core/components/DataGrid/DataGrid";
import { InformativeModal } from "@vega-core/components/Modals/InformativeModal/InformativeModal";
import { Notice, NoticeType } from "@vega-core/components/Notice/Notice";
import { m } from "@vega-core/paraglide/messages";
import { buildTimeFlags } from "@vega-core/utils/buildTimeFlags";
import { CoInvestmentFullPaths } from "@vega-modules/paths";

import { ShareTabType } from "../../constants/sharePageConstants";
import { useCoInvestDealReadOnly } from "../../hooks/useCoInvestDealReadOnly";
import { useExportFilename } from "../../hooks/useExportFilename";
import { useShareTableColumns } from "../../hooks/useShareTableColumns";
import { useShareTableManagement } from "../../hooks/useShareTableManagement";
import { useTableExport } from "../../hooks/useTableExport";
import { DraftEmailModal } from "../DraftEmail/DraftEmailModal";
import { OpportunityOwnerModal } from "../ProspectsTable/OpportunityOwnerModal";
import { ShareModal } from "../ShareModal/ShareModal";
import { ShareTableToolbar } from "../ShareTable/ShareTableToolbar";
import { UpdateDataPointsModal } from "../TrackingTable/components/UpdateDataPointsModal";

type ShareApprovedTableProps = {
  canAddShortlistItems?: boolean;
  canEditDealOpportunity?: boolean;
};

export const ShareApprovedTable = ({
  canAddShortlistItems,
  canEditDealOpportunity,
}: ShareApprovedTableProps) => {
  const {
    dealId,
    dealName,
    currency,
    prospectsData,
    paginationElement,
    isLoading,
    searchQuery,
    setSearchQuery,
    RenderFilters,
    rowSelection,
    onRowSelectionChange,
    showSelectedOnly,
    SelectOnlyToggle,
    isShareModalOpen,
    selectedProspectsForShare,
    handleShare,
    handleCloseShareModal,
    onShareModalSuccess,
    isOpportunityOwnerModalOpen,
    selectedProspectsForOwner,
    closeOpportunityOwnerModal,
    handleOpenOwnerModal,
    onOpportunityOwnerSuccess,
    isUpdateDataPointsModalOpen,
    closeUpdateDataPointsModal,
    dealOpportunityIdForEditingDataPoints,
    handleUpdateDataPoints,
    isCopyLinkNoticeOpen,
    dismissCopyLinkNotice,
    handleCopyLink,
    isDraftModalOpen,
    selectedProspectForDraft,
    handleOpenDraft,
    handleCloseDraftModal,
    onDraftSuccess,
    showRemoveConfirmation,
    selectedProspectForRemoval,
    handleRemoveIndividualProspect,
    handleShowIndividualRemoveConfirmation,
    closeRemoveConfirmation,
    bulkActions,
    showEmptyState,
    handleCellEdit,
  } = useShareTableManagement(ShareTabType.Approved, {
    canEditDealOpportunity,
  });

  const { columns, onCellKeyDown, renderColumnToggle, exportColumns } =
    useShareTableColumns({
      activeTab: ShareTabType.Approved,
      dealId,
      canAddShortlistItems,
      currency,
      onShare: handleShare,
      onDraft: handleOpenDraft,
      onRemoveProspect: handleShowIndividualRemoveConfirmation,
      removingProspectId: selectedProspectForRemoval,
      onUpdateDataPoints: buildTimeFlags.gp_co_investment_update_salesforce_data
        ? handleUpdateDataPoints
        : undefined,
      onCopyLink: handleCopyLink,
      onOpenOwnerModal: handleOpenOwnerModal,
    });

  const exportFilename = useExportFilename(dealId, "prospects");
  const { exportToCsv, exportToExcel, isExporting } = useTableExport({
    data: prospectsData,
    columns: exportColumns,
    filename: exportFilename,
  });

  const { isMutationLocked } = useCoInvestDealReadOnly();

  const navigate = useNavigate();
  const { data: sendDraftsCount = 0 } = useGetDealTeaserEmailsSummaryV2(
    dealId,
    {
      query: {
        enabled: !!dealId,
        select: (response) =>
          response.data.draftCount + response.data.scheduledCount,
      },
    },
  );

  const goToSendDrafts = () =>
    navigate(
      generatePath(CoInvestmentFullPaths.CoInvestmentShareSendDrafts, {
        id: dealId,
      }),
    );

  return (
    <>
      <div className="flex flex-col gap-4">
        {!showEmptyState && (
          <>
            {!isMutationLocked && SelectOnlyToggle}

            <ShareTableToolbar
              exportTestId="approved-export-menu"
              isExporting={isExporting}
              onExportCsv={exportToCsv}
              onExportExcel={exportToExcel}
              onSearchQueryChange={setSearchQuery}
              renderActions={
                !isMutationLocked && sendDraftsCount > 0 ? (
                  <ThemedButton
                    dataTestId="send-drafts-button"
                    icon={<MailIcon className="size-4" />}
                    iconPosition="left"
                    onClick={goToSendDrafts}
                    size="small"
                    variant="primary"
                  >
                    {m.coInvestments_sendDrafts_entryButton({
                      count: sendDraftsCount,
                    })}
                  </ThemedButton>
                ) : null
              }
              renderColumnToggle={renderColumnToggle}
              renderFilters={RenderFilters}
              searchDisabled={showSelectedOnly}
              searchName="approved-search"
              searchQuery={searchQuery}
            />
          </>
        )}
        <DataGrid<DealShortlistItemResponse>
          columnDefs={columns}
          emptyState={
            showEmptyState
              ? {
                  title: m.coInvestments_outreach_noProspects_title(),
                  illustration: <ListIllustration />,
                }
              : undefined
          }
          getRowId={(params) => params.data.id}
          loading={isLoading}
          onCellKeyDown={onCellKeyDown}
          onCellValueChanged={handleCellEdit}
          onSelectedRowsChange={onRowSelectionChange}
          rowData={prospectsData}
          rowSelectionOptions={{
            isRowSelectable: () => !isMutationLocked,
          }}
          selectedRows={rowSelection}
        />
        {paginationElement}
      </div>

      <InformativeModal
        description={m.coInvestments_prospects_remove_confirmModal_description()}
        isOpen={showRemoveConfirmation}
        onClose={closeRemoveConfirmation}
        primaryBtnProps={{
          children: m.btn_continue(),
          onClick: () => {
            if (selectedProspectForRemoval) {
              handleRemoveIndividualProspect(selectedProspectForRemoval);
            }
          },
          variant: "primary",
        }}
        secondaryBtnProps={{
          children: m.btn_cancel(),
          onClick: closeRemoveConfirmation,
        }}
        title={m.coInvestments_prospects_remove_confirmModal_title()}
        variant="error"
      />

      {selectedProspectsForShare.length > 0 ? (
        <ShareModal
          dealId={dealId}
          dealName={dealName}
          isOpen={isShareModalOpen}
          onClose={handleCloseShareModal}
          onSuccess={onShareModalSuccess}
          prospects={selectedProspectsForShare}
        />
      ) : null}

      {selectedProspectForDraft ? (
        <DraftEmailModal
          dealId={dealId}
          isOpen={isDraftModalOpen}
          onClose={handleCloseDraftModal}
          onSuccess={onDraftSuccess}
          prospect={selectedProspectForDraft}
        />
      ) : null}

      {isOpportunityOwnerModalOpen ? (
        <OpportunityOwnerModal
          closeModal={closeOpportunityOwnerModal}
          dealId={dealId}
          onSuccess={onOpportunityOwnerSuccess}
          open={isOpportunityOwnerModalOpen}
          selectedProspects={selectedProspectsForOwner}
        />
      ) : null}

      {dealOpportunityIdForEditingDataPoints ? (
        <UpdateDataPointsModal
          dealId={dealId}
          dealOpportunityId={dealOpportunityIdForEditingDataPoints}
          isOpen={isUpdateDataPointsModalOpen}
          onClose={closeUpdateDataPointsModal}
        />
      ) : null}

      {isCopyLinkNoticeOpen ? (
        <Notice
          action={
            <ThemedIconButton
              ariaLabel={m.btn_close()}
              className="ml-4"
              dataTestId="copy-link-dismiss"
              icon={<CloseIcon />}
              onClick={dismissCopyLinkNotice}
              variant="noBackground"
            />
          }
          content={m.coInvestments_outreach_copyLink_AccessInfo()}
          dataTestId="copy-link"
          type={NoticeType.PENDING}
        />
      ) : null}

      {bulkActions}
    </>
  );
};
